import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { supabaseDocsService } from '@/services/supabaseDocsService';
import { generateSlug, validateSlugUnique } from '@/utils/slugUtils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RichTextEditor } from '@/components/admin/RichTextEditor';
import { useToast } from '@/hooks/use-toast';
import { Loader2, ArrowLeft, Save, Send, Trash2 } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

const supportDocSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  slug: z.string().min(1, 'Slug is required'),
  content: z.string().min(1, 'Content is required'),
  category: z.string().min(1, 'Category is required'),
  subcategory: z.string().optional(),
  tags: z.string().optional(),
  status: z.enum(['draft', 'published', 'archived']).default('draft'),
  order_index: z.coerce.number().default(0),
  icon: z.string().optional(),
  featured_image_url: z.string().optional(),
});

type SupportDocFormData = z.infer<typeof supportDocSchema>;

export default function SupportDocEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(!!id);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const form = useForm<SupportDocFormData>({
    resolver: zodResolver(supportDocSchema),
    defaultValues: {
      title: '',
      slug: '',
      content: '',
      category: '',
      subcategory: '',
      tags: '',
      status: 'draft',
      order_index: 0,
      icon: '',
      featured_image_url: '',
    },
  });

  useEffect(() => {
    if (id) {
      fetchDoc();
    }
  }, [id]);

  const fetchDoc = async () => {
    if (!id) return;
    
    try {
      setIsFetching(true);
      const doc = await supabaseDocsService.fetchDocById(id);
      
      if (!doc) {
        toast({
          title: 'Error',
          description: 'Support doc not found',
          variant: 'destructive',
        });
        navigate('/admin/content');
        return;
      }

      form.reset({
        title: doc.title,
        slug: doc.slug,
        content: doc.content,
        category: doc.category,
        subcategory: doc.subcategory || '',
        tags: doc.tags?.join(', ') || '',
        status: doc.status,
        order_index: doc.order_index,
        icon: doc.icon || '',
        featured_image_url: doc.featured_image_url || '',
      });
    } catch (error) {
      console.error('Error fetching doc:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch support doc',
        variant: 'destructive',
      });
    } finally {
      setIsFetching(false);
    }
  };

  const onTitleChange = async (title: string) => {
    form.setValue('title', title);
    if (!id) {
      const slug = generateSlug(title);
      const { isUnique, suggestedSlug } = await validateSlugUnique(slug, 'support_docs');
      form.setValue('slug', isUnique ? slug : suggestedSlug || slug);
    }
  };

  const onSubmit = async (data: SupportDocFormData, status: 'draft' | 'published') => {
    try {
      setIsLoading(true);

      const tags = data.tags?.split(',').map(t => t.trim()).filter(Boolean) || [];

      const docData = {
        title: data.title,
        slug: data.slug,
        content: data.content,
        category: data.category,
        subcategory: data.subcategory,
        tags,
        status,
        order_index: data.order_index,
        icon: data.icon,
        featured_image_url: data.featured_image_url,
      };

      if (id) {
        await supabaseDocsService.updateDoc(id, docData);
        toast({
          title: 'Success',
          description: 'Support doc updated successfully',
        });
      } else {
        await supabaseDocsService.createDoc(docData);
        toast({
          title: 'Success',
          description: 'Support doc created successfully',
        });
      }

      navigate('/admin/content');
    } catch (error) {
      console.error('Error saving doc:', error);
      toast({
        title: 'Error',
        description: 'Failed to save support doc',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      await supabaseDocsService.deleteDoc(id);
      toast({
        title: 'Success',
        description: 'Support doc deleted successfully',
      });
      navigate('/admin/content');
    } catch (error) {
      console.error('Error deleting doc:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete support doc',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
      setShowDeleteDialog(false);
    }
  };

  if (isFetching) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin/content')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-3xl font-bold">{id ? 'Edit' : 'New'} Support Doc</h1>
        </div>
        {id && (
          <Button variant="destructive" onClick={() => setShowDeleteDialog(true)}>
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
        )}
      </div>

      <Form {...form}>
        <form className="space-y-6">
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Title *</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    onChange={(e) => onTitleChange(e.target.value)}
                    placeholder="Enter support doc title"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="slug"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Slug *</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="url-friendly-slug" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="content"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Content *</FormLabel>
                <FormControl>
                  <RichTextEditor
                    content={field.value}
                    onChange={field.onChange}
                    placeholder="Write your support doc content..."
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Getting Started, Features..." />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="subcategory"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Subcategory</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Optional subcategory" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="order_index"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Order Index</FormLabel>
                  <FormControl>
                    <Input type="number" {...field} placeholder="0" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="icon"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Icon</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Book, Shield..." />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="tags"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tags (comma-separated)</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="setup, configuration, troubleshooting" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="featured_image_url"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Featured Image URL</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="https://..." />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={form.handleSubmit((data) => onSubmit(data, 'draft'))}
              disabled={isLoading}
            >
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save as Draft
            </Button>
            <Button
              type="button"
              onClick={form.handleSubmit((data) => onSubmit(data, 'published'))}
              disabled={isLoading}
            >
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Publish
            </Button>
          </div>
        </form>
      </Form>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the support doc.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
